import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { publishersManageService } from '../../../api/publishersManageService';
import type {
  UpdatePublisherManageDto,
} from '../../../types/managePublishers';
import { useTranslation } from '../../../i18n/useTranslation';

interface Props {
  publisherId: string | null;
  onClose: () => void;
}

type Tab = 'metadata' | 'drm';

const emptyDraft: UpdatePublisherManageDto = {
  name: '',
  isActive: true,
  isDrmEnabled: false,
  alsoUpdateBooks: true,
  concurrencyStamp: '',
};

export default function PublisherEditDrawer({ publisherId, onClose }: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>('metadata');
  const [draft, setDraft] = useState<UpdatePublisherManageDto>(emptyDraft);
  const [bookCount, setBookCount] = useState<number | null>(null);
  const [conflict, setConflict] = useState(false);

  const query = useQuery({
    queryKey: ['manage-publisher', publisherId],
    queryFn: () => publishersManageService.get(publisherId!),
    enabled: publisherId !== null,
  });

  useEffect(() => {
    if (!query.data) return;
    setConflict(false);
    setDraft({
      name: query.data.name,
      contactEmail: query.data.contactEmail,
      contactPhone: query.data.contactPhone,
      website: query.data.website,
      isActive: query.data.isActive,
      isDrmEnabled: query.data.isDrmEnabled,
      defaultPrintQuotaLimit: query.data.defaultPrintQuotaLimit,
      defaultPrintQuotaWindowDays: query.data.defaultPrintQuotaWindowDays,
      defaultCopyQuotaLimit: query.data.defaultCopyQuotaLimit,
      defaultCopyQuotaWindowDays: query.data.defaultCopyQuotaWindowDays,
      defaultDownloadQuotaLimit: query.data.defaultDownloadQuotaLimit,
      defaultDownloadQuotaWindowDays: query.data.defaultDownloadQuotaWindowDays,
      alsoUpdateBooks: true,
      concurrencyStamp: query.data.concurrencyStamp,
    });
    setBookCount(query.data.bookCount);
  }, [query.data]);

  const save = useMutation({
    mutationFn: () => publishersManageService.update(publisherId!, draft),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['manage-publishers'] });
      qc.invalidateQueries({ queryKey: ['manage-publisher', publisherId] });
      onClose();
    },
    onError: (err: any) => {
      // ABP serializes BusinessException with details in `error.code` or `error.message`.
      // The "Publishers:ConcurrencyConflict" code is what backend throws on stale stamp.
      const code: string = err?.response?.data?.error?.code ?? '';
      const message: string = err?.response?.data?.error?.message ?? err?.message ?? '';
      if (code.includes('ConcurrencyConflict') || message.includes('ConcurrencyConflict')) setConflict(true);
    },
  });

  const restore = useMutation({
    mutationFn: () => publishersManageService.restore(publisherId!, true),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['manage-publishers'] });
      qc.invalidateQueries({ queryKey: ['manage-publisher', publisherId] });
      onClose();
    },
  });

  const reload = async () => {
    setConflict(false);
    await query.refetch();
  };

  if (publisherId === null) return null;

  const isDeleted = query.data?.isDeleted === true;

  return (
    <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose}>
      <aside
        className="absolute right-0 top-0 h-full w-full sm:w-[480px] bg-white shadow-xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between border-b border-neutral-200 px-4 py-3">
          <h2 className="text-lg font-semibold">{query.data?.name ?? t('App.Admin.Publishers.PublisherEditDrawer.LoadingTitle')}</h2>
          <button type="button" onClick={onClose} className="text-neutral-500 hover:text-neutral-900" aria-label={t('App.Admin.Publishers.PublisherEditDrawer.CloseAriaLabel')}>×</button>
        </header>

        <nav className="flex border-b border-neutral-200">
          {(['metadata', 'drm'] as const).map(tab_ => (
            <button
              key={tab_}
              type="button"
              onClick={() => setTab(tab_)}
              className={`px-4 py-2 text-sm ${tab === tab_ ? 'border-b-2 border-primary-600 text-primary-600' : 'text-neutral-600 hover:bg-neutral-50'}`}
            >
              {tab_ === 'metadata' ? t('App.Admin.Publishers.PublisherEditDrawer.TabMetadata') : t('App.Admin.Publishers.PublisherEditDrawer.TabDrm')}
            </button>
          ))}
        </nav>

        {conflict && (
          <div className="bg-amber-50 border-b border-amber-200 text-amber-900 text-sm px-4 py-2 flex items-center justify-between">
            <span>{t('App.Admin.Publishers.PublisherEditDrawer.ConflictBanner')}</span>
            <button type="button" onClick={reload} className="underline">{t('App.Admin.Publishers.PublisherEditDrawer.ConflictReload')}</button>
          </div>
        )}

        <div className="flex-1 overflow-auto p-4">
          {tab === 'metadata' && (
            <div className="space-y-3">
              <Field label={t('App.Admin.Publishers.PublisherEditDrawer.FieldName')}>
                <input className={textInput} value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
              </Field>
              <Field label={t('App.Admin.Publishers.PublisherEditDrawer.FieldContactEmail')}>
                <input className={textInput} type="email" value={draft.contactEmail ?? ''} onChange={(e) => setDraft({ ...draft, contactEmail: e.target.value || undefined })} />
              </Field>
              <Field label={t('App.Admin.Publishers.PublisherEditDrawer.FieldContactPhone')}>
                <input className={textInput} value={draft.contactPhone ?? ''} onChange={(e) => setDraft({ ...draft, contactPhone: e.target.value || undefined })} />
              </Field>
              <Field label={t('App.Admin.Publishers.PublisherEditDrawer.FieldWebsite')}>
                <input className={textInput} type="url" value={draft.website ?? ''} onChange={(e) => setDraft({ ...draft, website: e.target.value || undefined })} />
              </Field>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={draft.isActive} onChange={(e) => setDraft({ ...draft, isActive: e.target.checked })} />
                {t('App.Admin.Publishers.PublisherEditDrawer.FieldActive')}
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={draft.isDrmEnabled} onChange={(e) => setDraft({ ...draft, isDrmEnabled: e.target.checked })} />
                {t('App.Admin.Publishers.PublisherEditDrawer.FieldDrmEnabled')}
              </label>
            </div>
          )}

          {tab === 'drm' && (
            <div className="space-y-3">
              <p className="text-xs text-neutral-500">{t('App.Admin.Publishers.PublisherEditDrawer.DrmTabHint')}</p>
              <div className="grid grid-cols-2 gap-3">
                <NumField label={t('App.Admin.Publishers.PublisherEditDrawer.FieldPrintLimit')} v={draft.defaultPrintQuotaLimit} onChange={(v) => setDraft({ ...draft, defaultPrintQuotaLimit: v })} />
                <NumField label={t('App.Admin.Publishers.PublisherEditDrawer.FieldPrintWindow')} v={draft.defaultPrintQuotaWindowDays} onChange={(v) => setDraft({ ...draft, defaultPrintQuotaWindowDays: v })} />
                <NumField label={t('App.Admin.Publishers.PublisherEditDrawer.FieldCopyLimit')} v={draft.defaultCopyQuotaLimit} onChange={(v) => setDraft({ ...draft, defaultCopyQuotaLimit: v })} />
                <NumField label={t('App.Admin.Publishers.PublisherEditDrawer.FieldCopyWindow')} v={draft.defaultCopyQuotaWindowDays} onChange={(v) => setDraft({ ...draft, defaultCopyQuotaWindowDays: v })} />
                <NumField label={t('App.Admin.Publishers.PublisherEditDrawer.FieldDownloadLimit')} v={draft.defaultDownloadQuotaLimit} onChange={(v) => setDraft({ ...draft, defaultDownloadQuotaLimit: v })} />
                <NumField label={t('App.Admin.Publishers.PublisherEditDrawer.FieldDownloadWindow')} v={draft.defaultDownloadQuotaWindowDays} onChange={(v) => setDraft({ ...draft, defaultDownloadQuotaWindowDays: v })} />
              </div>
              <label className="flex items-start gap-2 text-sm">
                <input type="checkbox" checked={draft.alsoUpdateBooks} onChange={(e) => setDraft({ ...draft, alsoUpdateBooks: e.target.checked })} className="mt-0.5" />
                <span>
                  {t('App.Admin.Publishers.PublisherEditDrawer.AlsoUpdateBooks')}
                  {bookCount !== null && <span className="text-neutral-500"> {t('App.Admin.Publishers.PublisherEditDrawer.AlsoUpdateBooksCount', { count: bookCount })}</span>}
                </span>
              </label>
            </div>
          )}
        </div>

        <footer className="border-t border-neutral-200 px-4 py-3 flex justify-between items-center">
          <button type="button" onClick={onClose} className="text-sm text-neutral-600 hover:underline">{t('App.Admin.Publishers.PublisherEditDrawer.Cancel')}</button>
          <div className="flex gap-2">
            {isDeleted && (
              <button
                type="button"
                onClick={() => restore.mutate()}
                disabled={restore.isPending}
                className="px-3 py-1 border border-green-300 text-green-700 rounded hover:bg-green-50 disabled:opacity-50"
              >
                {t('App.Admin.Publishers.PublisherEditDrawer.Restore')}
              </button>
            )}
            <button
              type="button"
              onClick={() => save.mutate()}
              disabled={save.isPending || conflict}
              className="px-3 py-1 bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
            >
              {t('App.Admin.Publishers.PublisherEditDrawer.Save')}
            </button>
          </div>
        </footer>
      </aside>
    </div>
  );
}

const textInput = 'border border-neutral-300 rounded px-2 py-1 text-sm w-full';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs text-neutral-600">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function NumField({ label, v, onChange }: { label: string; v?: number; onChange: (v?: number) => void }) {
  return (
    <label className="text-xs text-neutral-600">
      {label}
      <input
        type="number"
        min={0}
        value={v ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? undefined : Number(e.target.value))}
        className="border border-neutral-300 rounded px-2 py-1 text-sm w-full mt-1"
      />
    </label>
  );
}
